<template>
  <div>
    <v-navigation-drawer
      v-model="isOpen"
      :width="$isMobile ? '100%' : '50%'"
      right
      fixed
      :permanent="isOpen"
      hide-overlay
      :class="{'z-index--100': true, 'pl-4': true, drawer: !isOpen}"
      style="overflow: visible;">
      <div class="pa-3 content" style="overflow: auto; height: 100vh;">
        <div class="d-flex" style="margin-top: 68px;">
          <v-btn
            dark
            fab
            small
            outlined
            color="primary"
            class="close mr-3"
            @click="togglePanel"
            style="position: absolute; left: -20px; background-color: #fff; z-index: 100;"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>

        <div v-show="!editTip" class="mb-4">
          <table class="tableCollection">
            <thead>
              <tr>
                <th></th>
                <th class="font-weight-regular header">{{ $t('income.collection.collectionDetailsPanel.headers.netSale') }}</th>
                <th class="font-weight-regular header">{{ $t('income.collection.collectionDetailsPanel.headers.collection') }}</th>
                <th class="font-weight-regular header">{{ $t('income.collection.collectionDetailsPanel.headers.invoice') }}</th>
                <th class="font-weight-regular header">{{ $t('income.collection.collectionDetailsPanel.headers.difference') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-bold">{{ $t('income.collection.collectionDetailsPanel.totalCash') }}</td>
                <td class="text-right">{{ cancellationByPlatform | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td class="text-right">{{ realCashCollectedAmount | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td>
                  <div class="d-flex align-center justify-end">
                    <span>{{ realCashInvoiceAmount | toFixNumber(2) | numberWithComma | prefix('$') }}</span>
                  </div>
                </td>
                <td class="text-right">{{ realCashDifferenceAmount | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
              </tr>
              <tr v-if="!hasData" style="display: none;"></tr>
              <tr v-else v-for="incomeType in selectedCharges.payments" :key="incomeType.field">
                <td>{{ incomeType.name }}</td>
                <td class="text-right">{{ incomeType.saleTotal | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td class="text-right">{{ incomeType.transactionTotal | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td>
                  <div class="d-flex align-center justify-end">
                    <span>{{ incomeType.invoiceTotal | toFixNumber(2) | numberWithComma | prefix('$') }}</span>
                    <v-tooltip
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          text
                          color="#0F6FFF"
                          @click="openInvoiceDialog(incomeType.income, incomeType.invoices)"
                          v-bind="attrs"
                          v-on="on"
                          small
                          class="invoiceButton"
                        >
                          <v-icon small>
                            $openDialog
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('income.collection.collectionDetailsPanel.applyInvoice') }}</span>
                    </v-tooltip>
                  </div>
                </td>
                <td class="text-right">{{ incomeType.difference | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="font-weight-bold">{{ $t('income.collection.collectionDetailsPanel.totalGeneral') }}</td>
                <td class="text-right">{{ totalGeneralSales | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td class="text-right">{{ totalGeneralCollected | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td></td>
                <td class="text-right">{{ totalGeneralDifference | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('income.collection.collectionDetailsPanel.otherIncome') }}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('income.collection.collectionDetailsPanel.tip') }}</td>
                <td class="text-right">{{ totalTipSales | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td>
                  <div class="d-flex align-center justify-end">
                    <span>{{ totalTipCollected | toFixNumber(2) | numberWithComma | prefix('$') }}</span>
                    <v-btn
                      icon
                      color="primary"
                      x-small
                      @click="openEditTip()"
                      class="ml-1"
                    >
                      <v-icon>mdi-pencil-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td></td>
                <td class="text-right">{{ tipDifference | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t('income.collection.collectionDetailsPanel.total') }}</td>
                <td class="text-right">{{ totalSales | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td class="text-right">{{ totalCollected | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
                <td></td>
                <td class="text-right">{{ totalDifference | toFixNumber(2) | numberWithComma | prefix('$') }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div v-show="editTip" class="mb-4">
          <v-btn
            text
            small
            @click="closeEditTip()"
            class="buttonBack"
          >
            <v-icon>mdi-arrow-left</v-icon>
            <span>{{ $t('income.collection.collectionDetailsPanel.editTip.buttonBack') }}</span>
          </v-btn>
          <h5 class="tipTitle">{{ $t('income.collection.collectionDetailsPanel.editTip.title') }}</h5>
          <div class="tipSubtitleContainer">
            <h6 class="tipSubtitle">{{ $t('income.collection.collectionDetailsPanel.editTip.subtitle') }}</h6>
          </div>
          <div class="tipFormContainer">
            <div class="d-flex justify-space-between mb-1">
              <v-text-field
                v-model="$v.tipForm.tip.$model"
                :label="$t('income.collection.collectionDetailsPanel.editTip.tip')"
                :error-messages="tipErrors"
                type="number"
                rounded
                append-icon="mdi-pencil-outline"
                outlined
                dense
                class="greenOutlinedInput tipInput"
              />
              <v-dialog
                @keydown.esc="dateModalTip=false"
                ref="dialogTip"
                v-model="dateModalTip"
                :return-value.sync="tipForm.date"
                width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="$v.tipForm.date.$model"
                    :label="$t('income.collection.collectionDetailsPanel.editTip.date')"
                    :error-messages="tipDateErrors"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    class="calendarTextField calendarTextFieldTipSize"
                  >
                    <v-icon slot="append" color="#01D28E">
                      mdi-calendar
                    </v-icon>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="tipForm.date"
                  scrollable
                  ref="picker"
                  color="primary"
                  :first-day-of-week="1"
                  :allowed-dates="allowedDatesTip"
                  reactive
                  no-title
                  class="pt-4"
                  :locale="locale">
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    color="primary"
                    @click="dateModalTip = false"
                    class="dateButton"
                  >
                    {{ $t('income.collection.collectionDetailsPanel.editTip.dateModal.cancel') }}
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="confirmCalendarDatesTip"
                    class="dateButton"
                  >
                    {{ $t('income.collection.collectionDetailsPanel.editTip.dateModal.ok') }}
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </div>
            <div class="d-flex justify-end">
              <Button class="addTipButton mb-5" @click="addTip()">
                <v-icon class="mr-1">mdi-plus</v-icon>
                <span>{{ $t('income.collection.collectionDetailsPanel.editTip.buttonAdd') }}</span>
              </Button>
            </div>
          </div>
          <div>
            <h5 class="tipTitle mb-6">{{ $t('income.collection.collectionDetailsPanel.editTip.history') }}</h5>
            <v-data-table
              :loading-text="tableTips.loadingText"
              :loading="tableTips.loading"
              :headers="tableTips.headers"
              :items="tableTips.data"
              :options="tableTips.options"
              hide-default-footer
              class="tipsTable"
              sort-by="">
              <template v-slot:[`item.captureDate`]="{ item }">
                <div>{{ $moment(item.captureDate).format('DD/MM/YYYY') }}</div>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <div>{{ item.amount | toFixNumber(2) | numberWithComma | prefix('$') }}</div>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog @keydown.esc="invoiceDialogOpen=false" v-model="invoiceDialogOpen" max-width="866px">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            color="transparent"
            small
            depressed
            @click="closeInvoiceDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-tabs v-model="tab" class="px-4">
          <v-tabs-slider></v-tabs-slider>

          <v-tab class="tab">
            {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.titleTab2') }}
          </v-tab>
          <v-tab class="tab">
            {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.titleTab1') }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab" class="tabs-items">
          <v-tab-item>
            <v-card-title class="justify-end">
              <div class="d-flex">
                <v-dialog
                  @keydown.esc="dateModal=false"
                  ref="dialog"
                  v-model="dateModal"
                  :return-value.sync="selectedCalendarDates"
                  width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateRangeText"
                      :label="$t('income.collection.collectionDetailsPanel.invoiceDialog.date')"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      class="calendarTextField"
                    >
                      <v-icon slot="append" color="#01D28E">
                        mdi-calendar
                    </v-icon>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="selectedCalendarDates"
                    scrollable
                    ref="picker"
                    color="primary"
                    range
                    :first-day-of-week="1"
                    :allowed-dates="allowedDates"
                    @input="orderDateRange"
                    reactive
                    no-title
                    class="pt-2"
                    :locale="locale">
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="dateModal = false"
                      class="dateButton"
                    >
                      {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.dateModal.cancel') }}
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="confirmCalendarDates()"
                      class="dateButton"
                    >
                      {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.dateModal.ok') }}
                    </v-btn>
                  </v-date-picker>
                </v-dialog>
                <Button class="filterButton" @click="openFilterDialog()">
                  <v-icon class="mr-1">mdi-filter</v-icon>
                  <span>{{ $t('income.collection.collectionDetailsPanel.invoiceDialog.buttonFilter') }}</span>
                </Button>
              </div>
            </v-card-title>
            <v-card-text class="d-flex flex-column align-center">
              <div v-if="!!tableInvoices.data.length" class="fullWidth">
                <v-data-table
                  v-model="tableInvoices.selected"
                  :loading-text="tableInvoices.title"
                  :loading="tableInvoices.loading"
                  :headers="tableInvoices.headers"
                  :items="tableInvoices.data"
                  :options="tableInvoices.options"
                  :server-items-length="tableInvoices.total"
                  :footer-props="{
                    'items-per-page-options': [5, 10, 15, 20]
                  }"
                  show-select
                  item-key="uuid"
                  class="invoiceTable"
                  @update:options="updateInvoicesTable"
                  sort-by="">
                  <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
                    <v-simple-checkbox
                      v-if="item.selectable"
                      :ripple="false"
                      :value="isSelected"
                      @input="select($event)"
                      :style="{ color: isSelected ? 'var(--v-primary-base) !important' : '' }"
                    ></v-simple-checkbox>
                    <div v-else class="primary--text">
                      Relacionada
                    </div>
                  </template>
                  <template v-slot:[`item.receiver`]="{ item }">
                    <div>
                      {{ [
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceId'), item.uuid].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceSource'), item.issuer && item.issuer.rfc].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceSourceName'), item.issuer && item.issuer.name].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceDate'), $moment(item.issuedAt).format('YYYY-MM-DD HH:mm:ss')].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceTotal'), `${formatCurrency(item.total)}${item.currency ? ' ' + item.currency : ''}`].join(': ')
                      ].join(', ') }}
                    </div>
                  </template>

                  <!-- <template v-slot:footer="">
                    <div class="d-flex footer justify-end mt-1">
                      <div class="footerText">{{ $t('income.collection.footerText') }}</div>
                      <div class="my-auto">
                        <v-select
                          v-model="tableInvoices.options.itemsPerPage"
                          :items="[5, 10, 15, 20, 25, 30]"
                          :append-icon="null"
                          hide-details
                          class="footerSelect" />
                      </div>
                      <div class="my-auto">
                        <v-pagination
                            v-model="tableInvoices.options.page"
                            :length="pageCount"
                            total-visible="5">
                        </v-pagination>
                      </div>
                    </div>
                  </template> -->
                </v-data-table>
              </div>
              <div v-else class="my-4 noInfo">
                {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.noInfo') }}
              </div>
            </v-card-text>
            <v-card-actions class="px-6 justify-end">
              <Button size="small" type="outlined" class="mr-1" @click="closeInvoiceDialog">
                {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.buttonCancel') }}
              </Button>
              <Button size="small" :disabled="(tableInvoices.data.length !== 0 && !selectableInvoices.length)" @click="applyInvoices()">
                {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.buttonApply') }}
              </Button>
            </v-card-actions>
          </v-tab-item>

          <v-tab-item>
            <v-card-text class="d-flex flex-column align-center">
              <div v-if="!!tableCollectedInvoices.data.length" class="fullWidth">
                <v-data-table
                  :loading-text="tableCollectedInvoices.loadingText"
                  :loading="tableCollectedInvoices.loading"
                  :headers="tableCollectedInvoices.headers"
                  :items="tableCollectedInvoices.data"
                  :options="tableCollectedInvoices.options"
                  :footer-props="{
                    'items-per-page-options': [5, 10, 15, 20]
                  }"
                  class="invoiceTable"
                >
                  <template v-slot:[`item.receiver`]="{ item }">
                    <div>
                      {{ [
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceId'), item.uuid].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceSource'), item.issuerTaxid].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceDate'), $moment(item.issuedAt).format('YYYY-MM-DD HH:mm:ss')].join(': '),
                        [$t('income.collection.collectionDetailsPanel.invoiceDialog.invoiceTotal'), `${formatCurrency(item.total)}${item.currency ? ' ' + item.currency : ''}`].join(': ')
                      ].join(', ') }}
                    </div>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <div>
                      <v-btn icon @click="deleteCollectedInvoice(incomeTypeId, item.uuid)">
                        <v-icon>mdi-trash-can</v-icon>
                      </v-btn>
                    </div>
                  </template>

                  <!-- <template v-slot:footer="">
                    <div class="d-flex footer justify-end mt-1">
                      <div class="footerText">{{ $t('income.collection.footerText') }}</div>
                      <div class="my-auto">
                        <v-select
                          v-model="tableCollectedInvoices.options.itemsPerPage"
                          :items="[5, 10, 15, 20, 25, 30]"
                          :append-icon="null"
                          hide-details
                          class="footerSelect" />
                      </div>
                      <div class="my-auto">
                        <v-pagination
                            v-model="tableCollectedInvoices.options.page"
                            :length="pageCount"
                            total-visible="5">
                        </v-pagination>
                      </div>
                    </div>
                  </template> -->
                </v-data-table>
              </div>
              <div v-else class="my-4 noInfo">
                {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.noInfo') }}
              </div>
            </v-card-text>
            <v-card-actions class="px-6 justify-end">
              <Button size="small" type="outlined" class="mr-1" @click="closeInvoiceDialog()">
                {{ $t('income.collection.collectionDetailsPanel.invoiceDialog.buttonClose') }}
              </Button>
            </v-card-actions>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="filterDialogOpen=false" v-model="filterDialogOpen" max-width="600px">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            color="transparent"
            small
            depressed
            @click="closeFilterDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title>
          <h3 class="titleFilterDialog">{{ $t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.title') }}</h3>
        </v-card-title>
        <v-card-text>
          <!-- <v-text-field
            v-model="dialogFilters.uuid"
            :label="$t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.uuid')"
            rounded
            append-icon="mdi-pencil-outline"
            clear-icon="mdi-close-circle"
            clearable
            hide-details
            outlined
            dense
            class="mt-4 greenOutlinedInput"
          /> -->
          <v-text-field
            v-model="dialogFilters.issuerName"
            :label="$t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.name')"
            rounded
            append-icon="mdi-pencil-outline"
            clear-icon="mdi-close-circle"
            clearable
            hide-details
            outlined
            dense
            class="mt-4 greenOutlinedInput"
          />
          <h6 class="totalFilterTitle">{{ $t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.total') }}</h6>
          <v-slider
            v-model="dialogFilters.totalMin"
            :max="50000"
            :min="0"
            hide-details
            :thumb-size="22"
            class="align-center slider"
          >
            <template v-slot:prepend>
              <v-text-field
                v-model="dialogFilters.totalMin"
                :label="$t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.totalMin')"
                class="mt-0 pt-0 sliderTextField"
                clearable
                clear-icon="mdi-close-circle"
                @click:clear="dialogFilters.totalMin = null"
                dense
                rounded
                hide-details
                outlined
                type="number"
              ></v-text-field>
            </template>
          </v-slider>
          <v-slider
            v-model="dialogFilters.totalMax"
            :max="50000"
            :min="0"
            hide-details
            :thumb-size="22"
            class="align-center slider"
          >
            <template v-slot:prepend>
              <v-text-field
                v-model="dialogFilters.totalMax"
                :label="$t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.totalMax')"
                class="mt-0 pt-0 sliderTextField"
                clearable
                clear-icon="mdi-close-circle"
                @click:clear="dialogFilters.totalMax = null"
                rounded
                hide-details
                outlined
                dense
                type="number"
              ></v-text-field>
            </template>
          </v-slider>
        </v-card-text>
        <v-card-actions class="px-6 justify-space-between">
          <Button size="small" @click="resetFilters()" class="resetButton">{{ $t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.resetFilters') }}</Button>
          <Button size="small" @click="applyFilters()">{{ $t('income.collection.collectionDetailsPanel.invoiceDialog.filterDialog.buttonSave') }}</Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import moment from 'moment'
import { isEmpty, isEqual } from 'lodash'
import { validationMixin } from 'vuelidate'
import { required, decimal, minValue } from 'vuelidate/lib/validators'

import Button from '@/components/elements/Button'
import getLocale from '@/lang/locale'
import formatCurrency from '@/utils/formatCurrency'

export default {
  name: 'CollectionDetails',
  props: {
    isOpen: Boolean,
    selectedCharges: Object,
    selectedDates: Array
  },
  components: {
    Button
  },
  emits: ['close', 'update:isOpen', 'updateCashClosings'],
  mixins: [validationMixin],
  validations: {
    tipForm: {
      tip: { required, decimal, minValue: minValue(0) },
      date: { required }
    }
  },
  data () {
    return {
      overlay: false,
      invoiceDialogOpen: false,
      incomeTypeId: '',
      dateModal: false,
      dateModalTip: false,
      selectedCalendarDates: [],
      tipForm: {
        tip: null,
        date: ''
      },
      tableInvoices: {
        title: this.$i18n.t('income.collection.collectionDetailsPanel.invoiceDialog.tableInvoices.loading'),
        loading: false,
        headers: [
          { text: '', value: 'receiver', sortable: false }
        ],
        data: [],
        total: 0,
        selected: [],
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 5,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [],
          sortDesc: []
        }
      },
      tableCollectedInvoices: {
        loadingText: this.$i18n.t('income.collection.collectionDetailsPanel.invoiceDialog.tableCollectedInvoices.loading'),
        loading: false,
        headers: [
          { text: '', value: 'receiver', sortable: false },
          { text: '', value: 'actions', sortable: false }
        ],
        data: [],
        total: 0,
        selected: [],
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 5,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [],
          sortDesc: []
        }
      },
      tableTips: {
        loadingText: this.$i18n.t('income.collection.collectionDetailsPanel.editTip.table.loadingText'),
        loading: false,
        headers: [
          { text: this.$i18n.t('income.collection.collectionDetailsPanel.editTip.table.headers.date'), value: 'amount' },
          { text: this.$i18n.t('income.collection.collectionDetailsPanel.editTip.table.headers.tip'), value: 'captureDate' }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['date'],
          sortDesc: []
        }
      },
      filterDialogOpen: false,
      dialogFilters: {
        uuid: '',
        issuerName: '',
        totalMin: null,
        totalMax: null
      },
      filters: {},
      editTip: false,
      tab: 0,
      charges: {} // Todo: ?
    }
  },
  computed: {
    open: {
      get: function () {
        return this.isOpen
      },
      set: function (newValue) {
        this.$emit('update:isOpen', newValue)
      }
    },
    locale () {
      return getLocale(this.$i18n.locale)
    },
    pageCount () {
      return this.tableInvoices.options.itemsPerPage ? Math.ceil(this.tableInvoices.total / this.tableInvoices.options.itemsPerPage) : 0
    },
    pageCountTableCollectedInvoices () {
      return this.tableCollectedInvoices.options.itemsPerPage ? Math.ceil(this.tableCollectedInvoices.total / this.tableCollectedInvoices.options.itemsPerPage) : 0
    },
    restaurantTaxid () {
      return this.$store.state.currentRestaurant.taxInformation.data.taxId || ''
    },
    dateRangeText () {
      return this.selectedCalendarDates.join(' ~ ')
    },
    totalGeneralSales () {
      let totalGeneral = 0
      if (!isEmpty(this.selectedCharges)) {
        if (!isEmpty(this.selectedCharges.totalEfectivoCaja)) {
          totalGeneral += this.selectedCharges.totalEfectivoCaja.saleTotal || 0
        }
        this.selectedCharges.payments.forEach(incomeType => {
          totalGeneral += incomeType.saleTotal || 0
        })
      }
      return totalGeneral
    },
    totalSales () {
      let total = this.totalGeneralSales
      if (!isEmpty(this.selectedCharges) && !isEmpty(this.selectedCharges.tips)) {
        total -= (this.selectedCharges.tips.tipTotal || 0)
      }
      return total
    },
    totalGeneralCollected () {
      let total = 0
      if (!isEmpty(this.selectedCharges)) {
        if (!isEmpty(this.selectedCharges.totalEfectivoCaja)) {
          total += this.selectedCharges.totalEfectivoCaja.transactionTotal || 0
        }
        this.selectedCharges.payments.forEach(incomeType => {
          total += incomeType.transactionTotal || 0
        })
      }
      return total
    },
    totalCollected () {
      let totalCollected = this.totalGeneralCollected
      if (!isEmpty(this.selectedCharges) && !isEmpty(this.selectedCharges.tips)) {
        totalCollected -= (this.selectedCharges.tips.capturedTipTotal || 0)
      }
      return totalCollected
    },
    totalGeneralDifference () {
      return this.totalGeneralSales - this.totalGeneralCollected
    },
    totalTipSales () {
      let total = 0
      if (!isEmpty(this.selectedCharges) && !isEmpty(this.selectedCharges.tips)) {
        total = this.selectedCharges.tips.tipTotal || 0
      }
      return total
    },
    totalTipCollected () {
      let total = 0
      if (!isEmpty(this.selectedCharges) && !isEmpty(this.selectedCharges.tips)) {
        total += (this.selectedCharges.tips.capturedTipTotal || 0)
      }
      return total
    },
    tipDifference () {
      let tipDifference = 0
      if (!isEmpty(this.selectedCharges) && !isEmpty(this.selectedCharges.tips)) {
        tipDifference = (this.selectedCharges.tips.tipTotal || 0) - (this.selectedCharges.tips.capturedTipTotal || 0)
      }
      return tipDifference
    },
    totalDifference () {
      return this.totalSales - this.totalCollected
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    hasData () {
      return !isEmpty(this.selectedCharges)
    },
    realCashSalesAmount () {
      return (!isEmpty(this.selectedCharges) && this.selectedCharges.totalEfectivoCaja && this.selectedCharges.totalEfectivoCaja.saleTotal) || 0
    },
    cancellationByPlatform () {
      return this.selectedCharges.cancellationByPlatform || 0
    },
    realCashCollectedAmount () {
      return (!isEmpty(this.selectedCharges) && this.selectedCharges.totalEfectivoCaja && this.selectedCharges.totalEfectivoCaja.transactionTotal) || 0
    },
    realCashInvoiceAmount () {
      return (!isEmpty(this.selectedCharges) && this.selectedCharges.totalEfectivoCaja && this.selectedCharges.totalEfectivoCaja.invoiceTotal) || 0
    },
    realCashDifferenceAmount () {
      return (!isEmpty(this.selectedCharges) && this.selectedCharges.totalEfectivoCaja && this.selectedCharges.totalEfectivoCaja.difference) || 0
    },
    tipDateErrors () {
      const errors = []
      if (!this.$v.tipForm.date.$dirty) return errors
      !this.$v.tipForm.date.required && errors.push(this.$i18n.t('income.collection.collectionDetailsPanel.editTip.validations.required'))
      return errors
    },
    tipErrors () {
      const errors = []
      if (!this.$v.tipForm.tip.$dirty) return errors
      !this.$v.tipForm.tip.minValue && errors.push(this.$i18n.t('income.collection.collectionDetailsPanel.editTip.validations.positive'))
      !this.$v.tipForm.tip.decimal && errors.push(this.$i18n.t('income.collection.collectionDetailsPanel.editTip.validations.number'))
      !this.$v.tipForm.tip.required && errors.push(this.$i18n.t('income.collection.collectionDetailsPanel.editTip.validations.required'))
      return errors
    },
    selectableInvoices () {
      const selectableInvoices = this.tableInvoices.selected.filter(item => item.selectable)
      const invoicesId = selectableInvoices.map(item => item.id)
      return invoicesId
    }
  },
  watch: {
    invoiceDialogOpen: async function (newValue) {
      if (newValue) {
        this.selectedCalendarDates = this.selectedDates
        await this.refreshTableInvoices()
      } else {
        this.tab = 0
      }
    },
    'tableInvoices.options.page': function () {
      this.selectedCalendarDates.length === 2 && this.loadInvoices(this.filters)
    },
    'tableInvoices.options.itemsPerPage': function () {
      this.tableInvoices.options.page = 1
      this.selectedCalendarDates.length === 2 && this.loadInvoices(this.filters)
    },
    tab: async function (val) {
      if (val === 0) {
        await this.refreshTableInvoices()
      }
    },
    selectedCharges: {
      handler (value) {
        if (this.incomeTypeId && !isEmpty(value)) {
          const selectedIncomeType = this.selectedCharges.payments.find(item => item.income === this.incomeTypeId)
          this.tableCollectedInvoices.data = selectedIncomeType.invoices || []
          this.tableCollectedInvoices.total = selectedIncomeType.length
        }
      },
      deep: true
    }
  },
  mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  methods: {
    keyDownHandler (event) {
      if (event.code === 'Escape') {
        if (!this.open) {
          this.$emit('updateCharges')
        }
        this.open = !this.open
      }
    },
    async updateInvoicesTable (data) {
      if (!isEqual(data, this.tableInvoices.options)) {
        this.tableInvoices.options = data
        await this.loadInvoices()
      }
    },
    togglePanel () {
      if (!this.open) {
        this.$emit('updateCharges')
      }
      this.open = !this.open
    },
    openInvoiceDialog (incomeTypeId, incomeTypeInvoices) {
      this.incomeTypeId = incomeTypeId || ''
      this.tableCollectedInvoices.data = incomeTypeInvoices || []
      this.invoiceDialogOpen = true
    },
    closeInvoiceDialog () {
      this.resetInvoiceDialog()
      this.invoiceDialogOpen = false
    },
    applyInvoices: async function (params = null) {
      params = params || {}
      this.overlay = true
      try {
        const invoicesId = this.selectableInvoices
        const promisesArray = invoicesId.map(item =>
          new Promise((resolve, reject) => {
            this.getInvoice(item).then(response => {
              resolve(response)
            }).catch(error => {
              reject(error)
            })
          })
        )

        Promise.all(promisesArray).then(invoicesXml => {
          this.relateInvoices(this.incomeTypeId, invoicesXml)
        }).catch(error => {
          this.$toasted.show(error.message, { duration: 3000 })
        })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    allowedDates (val) {
      const topDate = new Date()
      const evalDate = new Date(moment(val).format('YYYY-MM-DDTHH:mm:ss.SSS'))

      return evalDate.getTime() <= topDate.getTime()
    },
    allowedDatesTip (val) {
      const [startDate, endDate] = this.selectedDates
      const start = new Date(moment(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS'))
      const end = new Date(moment(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS'))
      const evalDate = new Date(moment(val).format('YYYY-MM-DDTHH:mm:ss.SSS'))

      return evalDate.getTime() <= end.getTime() && evalDate.getTime() >= start.getTime()
    },
    orderDateRange () {
      this.selectedCalendarDates.sort((a, b) => new Date(a) - new Date(b))
    },
    confirmCalendarDates () {
      this.$refs.dialog.save(this.selectedCalendarDates)
      this.refreshTableInvoices()
    },
    confirmCalendarDatesTip () {
      this.$refs.dialogTip.save(this.tipForm.date)
    },
    loadInvoices: async function (params = null) {
      const [startDate, endDate] = this.selectedCalendarDates
      params = {
        startDate: this.$moment(startDate).startOf('day').toISOString(),
        endDate: this.$moment(endDate).endOf('day').toISOString(),
        rfc: this.restaurantTaxid,
        limit: this.tableInvoices.options.itemsPerPage,
        isIssuer: false,
        type: 'I',
        page: this.tableInvoices.options.page,
        ...this.filters
      }

      this.tableInvoices.loading = true
      try {
        const response = await this.$api.get(`/v1/invoices`, params)
        this.tableInvoices.data = response.items
        this.tableInvoices.total = response.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.tableInvoices.loading = false
    },
    getInvoice: async function (invoiceId) {
      const { invoice } = await this.$api.get(`/v1/invoices/${invoiceId}/cfdi`)
      return invoice
    },
    relateInvoices: async function (incomeTypeId, xmlInvoices) {
      const payload = {
        restaurant: this.restaurant._id,
        invoices: xmlInvoices
      }

      this.overlay = true
      try {
        await this.$api.post(`/v1/charges/income/${incomeTypeId}/relate-invoice`, payload)
        await this.$emit('updateCollections')
        await this.$emit('updateCharges')
        this.closeInvoiceDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    openFilterDialog () {
      this.filterDialogOpen = true
    },
    closeFilterDialog () {
      this.filterDialogOpen = false
    },
    applyFilters () {
      this.filters = {}
      for (const filter in this.dialogFilters) {
        if (this.dialogFilters[filter]) {
          this.filters[filter] = this.dialogFilters[filter]
        }
      }
      this.refreshTableInvoices()
      this.closeFilterDialog()
    },
    resetFilters () {
      this.dialogFilters = {
        uuid: '',
        issuerName: '',
        totalMin: null,
        totalMax: null
      }
      this.filters = {}
    },
    resetInvoiceDialog () {
      this.tableInvoices.data = []
      this.tableInvoices.selected = []
      this.tableInvoices.options.page = 1
      this.tableInvoices.total = 0
      this.tableCollectedInvoices.data = []
      this.tableCollectedInvoices.selected = []
      this.tableCollectedInvoices.options.page = 1
      this.tableCollectedInvoices.total = 0
      this.incomeTypeId = ''
      this.resetFilters()
    },
    openEditTip () {
      this.loadTips()
      this.editTip = true
    },
    closeEditTip () {
      this.editTip = false
      this.resetTipForm()
    },
    loadTips: async function () {
      const [startDate, endDate] = this.selectedDates
      const payload = {
        restaurant: this.restaurant._id,
        'captureDate>': startDate,
        'captureDate<': endDate
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/charge-tip`, payload)
        this.tableTips.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    createTip: async function () {
      const payload = {
        restaurant: this.restaurant._id,
        amount: this.tipForm.tip,
        captureDate: this.tipForm.date
      }
      this.overlay = true
      try {
        await this.$api.post(`/v1/charge-tip`, payload)
        this.loadTips()
        this.$emit('updateCharges')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    addTip () {
      this.$v.tipForm.$touch()

      if (!this.$v.tipForm.$invalid) {
        this.createTip()
      }
    },
    resetTipForm () {
      this.$v.tipForm.$reset()
    },
    formatCurrency,
    deleteCollectedInvoice: async function (incomeTypeId, invoiceUuid) {
      const payload = {}
      this.overlay = true
      try {
        await this.$api.del(`/v1/charges/income/${incomeTypeId}/unrelate-invoice/${invoiceUuid}`, payload)
        await this.$emit('updateCollections')
        await this.$emit('updateCharges')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    refreshTableInvoices () {
      this.tableInvoices.total = 0
      this.selectedCalendarDates.length === 2 && this.loadInvoices(this.filters)
      this.tableInvoices.options.page = 1
    }
  }
}

</script>

<style lang="scss" scoped>
  .content {
    overflow: visible;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .tableCollection {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0px;

    .header {
      font-size: 1.143rem;
    }

    & tr {
      & th {
        padding: 22px 8px;
        background-color: #fff;

        &:first-child {
          padding: 22px 8px 22px 18px;
        }

        &:last-child {
          padding: 22px 18px 22px 8px;
        }
      }

      & td {
        padding: 22px 8px;
        background-color: #fff;
        border-top: 1px solid #ACABAB;
        border-bottom: 1px solid #ACABAB;

        &:first-child {
          padding: 22px 8px 22px 18px;
          border-top-left-radius: 7px;
          border-bottom-left-radius: 7px;
          border-left: 1px solid #ACABAB;
        }

        &:last-child {
          padding: 22px 18px 22px 8px;
          border-top-right-radius: 7px;
          border-bottom-right-radius: 7px;
          border-right: 1px solid #ACABAB;
        }
      }
    }

    & tfoot tr {
      & td {
        background-color: #f9f9f9;
      }
    }
  }

  .v-pagination__navigation {
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  .v-pagination__item {
    -webkit-box-shadow: none;
    box-shadow: none;
    color: #A4A6AB !important;
  }

  .v-pagination__item--active {
    color: #fff !important;
  }

  .footer {
    font-size: 12px;
    color: #A4A6AB;
  }

  .footerText {
    margin-right: 12px;
    margin-top: auto;
    margin-bottom: auto;
  }

  .footerSelect {
    width: 30px;
    padding-top: 0px;
    margin-top: 0px;
    color: #A4A6AB;
  }
  .footerSelect.v-text-field>.v-input__control>.v-input__slot:before {
    border-style: none;
  }
  .footerSelect.v-text-field>.v-input__control>.v-input__slot:after {
      border-style: none;
  }

  .footerSelect .v-select__selections {
    color: #A4A6AB !important;
  }

  .drawer {
    transform: translateX(calc(100% - 22px)) !important;
  }

  .titleFilterDialog {
    font-size: 1rem;
    font-weight: 500;
    color: #0F6FFF;
  }

  .dateButton {
    text-transform: none;
  }

  .greenOutlinedInput {
    &.error--text {
      & ::v-deep fieldset {
        border: 2px solid red !important;
      }
    }

    & ::v-deep .v-label {
      top: auto;

      &--active {
        top: 10px !important;
      }
    }

    & ::v-deep .v-input__slot {
      padding-right: 8px !important;
    }

    & ::v-deep .v-input__append-inner {
      margin-top: 4px !important;
    }

    & ::v-deep fieldset {
      border: 2px solid #01D28E;
    }

    & ::v-deep .v-input__icon--append {
      color: #01D28E;
    }
  }

  .noInfo {
    width: 100%;
    border: 1px solid #ACABAB;
    border-radius: 7px;
    padding-top: 22px;
    padding-bottom: 22px;
    text-align: center;
    line-height: 19px;
    color: #ACABAB;
  }

  .fullWidth {
    width: 100%;
  }

  .calendarTextField {
    height: 36px;
    width: 240px;
    margin-right: 11px;
    border-radius: 22px;

    & ::v-deep .v-input__slot {
      min-height: 36px !important;
    }

    & ::v-deep .v-label--active {
      padding-right: 4px;
      background-color: #fff;
    }

    & ::v-deep .v-label {
      top: auto;
    }

    & ::v-deep fieldset {
      border: 2px solid #01D28E;
    }

    &.error--text {
      & ::v-deep fieldset {
        border: 2px solid red !important;
      }

      & ::v-deep .v-icon {
        color: red !important;
      }
    }
  }

  .calendarTextFieldTipSize {
    max-width: 240px;
    margin-right: 0 !important;

    & ::v-deep fieldset {
      height: 37px;
    }
  }

  .tipInput {
    width: 240px;
    max-width: 240px;
  }

  .invoiceButton {
    min-width: inherit !important;
    padding: 0 4px !important;
    margin-left: 2px;
  }

  .filterButton {
    padding: 0 36px !important;
    background-color: #0F6FFF !important;
  }
  .addTipButton {
    background-color: #0F6FFF !important;
  }

  .totalFilterTitle {
    margin-top: 26px;
    margin-bottom: 14px;
    font-weight: 500;
    font-size: 0.875rem;
    color: #01D28E;
  }

  .slider {
    margin-bottom: 8px;

    & ::v-deep .v-slider__track-container {
      height: 9px;

      & .v-slider__track-fill {
        background-color: #0F6FFF !important;
        border-radius: 10px;
      }

      & .v-slider__track-background {
        background-color: #D7D7D7 !important;
        border-radius: 10px;
      }
    }

    & ::v-deep .v-slider__thumb-container{
      & .v-slider__thumb {
        width: 22px;
        height: 22px;
        background-color: #FFFFFF !important;
        box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.15);
        z-index: 10;

        &:before {
          top: 11px;
          left: 11px;
          width: 0;
          height: 0;
          color: #0F6FFF;
          background: #0F6FFF;
        }
      }

      &--focused .v-slider__thumb:before {
        top: 0;
        left: 0;
        width: 22px;
        height: 22px;
      }

      &:hover .v-slider__thumb:before {
        top: 0;
        left: 0;
        width: 22px;
        height: 22px;
      }
    }
  }

  .sliderTextField {
    max-width: 224px;

    & ::v-deep .v-label {
      top: auto;

      &--active {
        top: 10px !important;
      }
    }

    & ::v-deep .v-input__slot {
      padding-right: 8px !important;
    }

    & ::v-deep .v-input__append-inner {
      margin-top: 4px !important;
    }

    & ::v-deep .v-input__icon--append {
      color: #01D28E;
    }

    & ::v-deep .v-label--active {
      padding-right: 4px;
      background-color: #fff;
    }

    & ::v-deep fieldset {
      border: 2px solid #01D28E;
    }
  }

  .resetButton {
    background-color: #F03434 !important;
  }

  .buttonBack {
    margin-bottom: 12px;
    padding: 0 !important;
    color: #646464;
    text-transform: none;
  }

  .tipTitle {
    margin-bottom: 8px;
    font-size: 1.375rem;
    font-weight: 500;
    color: #01D28E;
  }

  .tipSubtitleContainer {
    padding-bottom: 25px;
  }

  .tipSubtitle {
    font-size: 1rem;
    font-weight: 500;
  }

  .tipFormContainer {
    margin-right: 59px;
  }

  .tabs {
    &:hover {
      cursor: pointer;
    }
  }

  .tab {
    font-size: 16px;
  }

  .tabs-items {
    width: 100%;
  }
</style>

<style lang="scss">
  .v-dialog > .v-card > .v-card__actions {
    padding: 0 16px 24px;
  }

  .invoiceTable {
    & .v-data-table__wrapper {
      max-height: 59vh;
      overflow-y: auto;
    }

    & tr {
      border: 1px solid #ACABAB !important;
      border-radius: 7px;
    }
    & td:first-child {
      border-left: 1px solid #ACABAB;
      border-top-left-radius: 7px;
      border-bottom-left-radius: 7px;
    }
    & td {
      border-top: 1px solid #ACABAB;
      border-bottom: 1px solid #ACABAB;
    }
    & td:last-child {
      border-right: 1px solid #ACABAB;
      border-top-right-radius: 7px;
      border-bottom-right-radius: 7px;
    }
    & thead tr:last-child th {
      border-bottom: none !important;
    }
  }

  .datePicker {
    box-shadow: none;
    -webkit-box-shadow: none;
  }

  .tipsTable {
    overflow-y: auto;
    max-height: 65vh;
  }

  .tipsTable tr {
    border: 1px solid #ACABAB;
    border-radius: 7px;
  }

  .tipsTable td:first-child {
    border-left: 1px solid #ACABAB;
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
  }

  .tipsTable td {
    border-top: 1px solid #ACABAB;
    border-bottom: 1px solid #ACABAB;
  }

  .tipsTable td:last-child {
    border-right: 1px solid #ACABAB;
    border-top-right-radius: 7px;
    border-bottom-right-radius: 7px;
  }

  .tipsTable thead tr:last-child th {
    border-bottom: none !important;
  }

</style>
